import { Link } from 'react-router-dom';
import {
  AlarmClock,
  AlertOctagon,
  AtSign,
  Bell,
  CheckCircle2,
  Eye,
  Mail,
  MailOpen,
  MessageSquare,
  Send,
  StickyNote,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';
import type { Notification, NotificationType } from '@/lib/queries';
import { notificationAccent } from '@/lib/format';
import { RelativeTime } from '@/components/RelativeTime';
import { MentionText } from '@/components/MentionText';
import { cn } from '@/lib/utils';

// The one icon map. The bell and the /notifications page each used to carry a
// copy, and they had drifted (`added_as_watcher` had an icon only in the
// bell). Leave-* types have no icon of their own on purpose — they fall back
// to the bell glyph; keep the palette minimal until the team asks for more.
const ICON_BY_TYPE: Partial<Record<NotificationType, LucideIcon>> = {
  client_replied: MessageSquare,
  sla_breach: AlarmClock,
  follow_up_sent: Send,
  follow_up_reminder: AlarmClock,
  reassigned_to_you: UserPlus,
  new_ticket_assigned: UserPlus,
  ticket_escalated: AlertOctagon,
  ticket_resolved: CheckCircle2,
  note_added: StickyNote,
  note_mention: AtSign,
  added_as_watcher: Eye,
};

interface Props {
  notification: Notification;
  /** `compact` for the bell panel, `comfortable` for the full page. */
  density: 'compact' | 'comfortable';
  /** Row clicked: mark read + navigate. */
  onActivate: (n: Notification) => void;
  /** Toggle button clicked: flip read/unread and do nothing else. */
  onToggleRead: (n: Notification) => void;
}

export function NotificationItem({ notification, density, onActivate, onToggleRead }: Props) {
  const Icon = ICON_BY_TYPE[notification.type] ?? Bell;
  const isUnread = notification.read_at === null;
  const accent = notificationAccent(notification.type);
  const comfortable = density === 'comfortable';

  const inner = (
    <div className={cn('flex items-start gap-3 px-3', comfortable ? 'py-3' : 'py-2.5')}>
      <span
        className={cn(
          'mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md',
          accent,
        )}
      >
        <Icon className="h-3.5 w-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          {isUnread ? (
            <span
              aria-hidden
              className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-tint-critical-fg"
            />
          ) : null}
          <p
            className={cn(
              'truncate text-sm leading-tight',
              isUnread ? 'font-semibold text-foreground' : 'font-medium text-foreground/90',
            )}
          >
            {/* Read state is otherwise purely visual (dot + weight). */}
            {isUnread ? <span className="sr-only">Unread. </span> : null}
            {notification.title}
          </p>
        </div>
        {notification.body ? (
          <p
            className={cn(
              'text-xs text-muted-foreground',
              comfortable ? 'mt-1 line-clamp-2' : 'mt-0.5 truncate',
            )}
          >
            <MentionText text={notification.body} />
          </p>
        ) : null}
        <p className={cn('text-2xs text-muted-foreground', comfortable ? 'mt-1.5' : 'mt-1')}>
          <RelativeTime value={notification.created_at} />
        </p>
      </div>
    </div>
  );

  // `ring-inset`: the panel clips overflow, so an outset focus ring would be
  // cut off on the edges — and keyboard focus is now a real path in here.
  const rowClasses = cn(
    'block min-w-0 flex-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
    comfortable && 'cursor-pointer',
  );

  // No ticket to open (leave notices): still a button, so a click marks it
  // read and clears the badge.
  const row = notification.ticket_id ? (
    <Link
      to={`/tickets/${notification.ticket_id}`}
      onClick={() => onActivate(notification)}
      className={rowClasses}
    >
      {inner}
    </Link>
  ) : (
    <button
      type="button"
      onClick={() => onActivate(notification)}
      className={cn(rowClasses, 'text-left')}
    >
      {inner}
    </button>
  );

  const ToggleIcon = isUnread ? MailOpen : Mail;
  const toggleLabel = isUnread ? 'Mark as read' : 'Mark as unread';

  return (
    // Backgrounds live on the <li> so they span the toggle too. The toggle is
    // a SIBLING of the row link, never a child: a <button> inside an <a> is
    // invalid HTML and would navigate on click. Same hover-action shape as
    // TicketCard's row actions.
    <li
      className={cn(
        'group/notif flex items-stretch transition-colors',
        isUnread ? 'bg-accent/40' : 'bg-transparent',
        notification.ticket_id && 'hover:bg-accent/60',
      )}
    >
      {row}
      <button
        type="button"
        onClick={() => onToggleRead(notification)}
        // `title` for the hover hint plus an aria-label that names the row so
        // a screen reader hears WHICH notification, not 40 identical buttons.
        // (Deliberately not a Radix tooltip: an open tooltip is the top
        // dismissable layer, so Escape would need two presses to close the
        // panel — and the page can mount 200 rows.)
        title={toggleLabel}
        aria-label={`${toggleLabel}: ${notification.title}`}
        className={cn(
          'flex w-9 shrink-0 cursor-pointer items-center justify-center text-muted-foreground transition-opacity hover:text-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
          // Hidden until the row is hovered or anything inside it has focus;
          // always shown on touch, where there is no hover to reveal it.
          'opacity-0 group-hover/notif:opacity-100 group-focus-within/notif:opacity-100 pointer-coarse:opacity-100',
        )}
      >
        <ToggleIcon className="h-4 w-4" aria-hidden />
      </button>
    </li>
  );
}
